/**
 * Утилиты для работы с файлами в Git-модуле.
 */
import type { FileTreeNode, GitFileRef, SelectedFilesState } from '../types';

/** Строит полный путь к файлу из path и name */
export function buildFullPath(path: string, name: string): string {
  return path ? `${path}/${name}` : name;
}

/** Возвращает язык для подсветки синтаксиса по расширению файла */
export function getLanguageForFile(fileName: string): string {
  const extension = fileName.split('.').pop()?.toLowerCase();
  const languageMap: Record<string, string> = {
    ts: 'typescript',
    tsx: 'typescript',
    js: 'javascript',
    jsx: 'javascript',
    php: 'php',
    py: 'python',
    kt: 'kotlin',
    xml: 'xml',
    gradle: 'groovy',
    md: 'markdown',
    json: 'json',
    css: 'css',
    scss: 'scss',
    html: 'html',
    java: 'java',
    cpp: 'cpp',
    c: 'c',
    cs: 'csharp',
    rb: 'ruby',
    go: 'go',
    rs: 'rust',
    swift: 'swift',
    yml: 'yaml',
    yaml: 'yaml',
  };
  return languageMap[extension ?? ''] ?? 'text';
}

/** Подсчитывает количество файлов в поддереве папки */
export function countFilesInSubtree(folder: FileTreeNode | null | undefined): number {
  if (!folder || !Array.isArray(folder.children)) return 0;
  let count = 0;
  folder.children.forEach((child) => {
    if (child.type === 'file') count += 1;
    else if (child.type === 'folder') count += countFilesInSubtree(child);
  });
  return count;
}

/** Собирает все файлы в папке рекурсивно */
export function addAllFilesInFolder(folder: FileTreeNode, folderPath: string): GitFileRef[] {
  if (!folder?.children) return [];
  const filesToAdd: GitFileRef[] = [];
  folder.children.forEach((child) => {
    const childPath = folderPath ? `${folderPath}/${child.name}` : child.name;
    if (child.type === 'file') {
      filesToAdd.push({ path: folderPath, name: child.name });
    } else if (child.type === 'folder' && child.children) {
      filesToAdd.push(...addAllFilesInFolder(child, childPath));
    }
  });
  return filesToAdd;
}

/** Собирает файлы с флагом chosen из дерева */
export function traverseForChosenFiles(items: FileTreeNode[], path = ''): GitFileRef[] {
  const chosenFiles: GitFileRef[] = [];
  items.forEach((item) => {
    const currentPath = path ? `${path}/${item.name}` : item.name;
    if (item.type === 'file' && item.chosen) {
      chosenFiles.push({ path, name: item.name });
    } else if (item.type === 'folder' && Array.isArray(item.children) && item.children.length > 0) {
      chosenFiles.push(...traverseForChosenFiles(item.children, currentPath));
    }
  });
  return chosenFiles;
}

/** Подсчитывает выбранные файлы под путём папки */
export function countSelectedUnderPath(
  selectedFiles: SelectedFilesState | null | undefined,
  folderPath: string
): number {
  if (!selectedFiles || !Array.isArray(selectedFiles.files)) return 0;
  return selectedFiles.files.filter(
    (f) => f.name && (f.path === folderPath || (f.path && f.path.startsWith(folderPath + '/')))
  ).length;
}

/** Собирает Set ключей всех файлов в дереве */
export function getExistingFileKeys(
  items: FileTreeNode[],
  path = '',
  result = new Set<string>()
): Set<string> {
  items.forEach((item) => {
    const currentPath = path ? `${path}/${item.name}` : item.name;
    if (item.type === 'file') {
      result.add(`${path}|${item.name}`);
    } else if (item.type === 'folder' && Array.isArray(item.children)) {
      getExistingFileKeys(item.children, currentPath, result);
    }
  });
  return result;
}
